/**
 * First-visit guided tour for /home + the global sidebar.
 *
 * Pattern is intentionally identical to BlotOnboardingTour (welcome modal →
 * coachmark sequence with spotlight cutout → finish modal). Differences are
 * only cosmetic (purple/pink brand palette to align with the HomeHeader
 * gradient) and the steps themselves, which walk the user through:
 *
 *   1. Hero greeting + plan badge        (home-hero)
 *   2. In-progress cases priority pane   (home-cases)
 *   3. Activity dashboard / KPIs         (home-activity)
 *   4. Sidebar module navigation         (sidebar-nav)
 *   5. Sidebar quick-access menu (+)     (sidebar-quick)
 *   6. Aletheia AI chat floating button  (aletheia-fab)
 *   7. User menu (profile / plan / out)  (sidebar-user)
 *
 * Auto-opens once per device after the AI Act notice is acknowledged.
 * Persists via localStorage key `careflai.homeTourCompleted.v1` (versioned
 * so we can re-run the tour for everyone by bumping the suffix).
 */
import {
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LuArrowRight,
  LuCircleCheck,
  LuClipboardList,
  LuCompass,
  LuLayoutDashboard,
  LuMessageSquare,
  LuMousePointerClick,
  LuPlus,
  LuRocket,
  LuSparkles,
  LuCircleUser,
  LuX,
  LuZap,
} from 'react-icons/lu'
import { hasAcknowledgedAIAct } from '../_shared/AIActNotice'
import { useUiPreferences, useUpdateUiPreferences } from '../../api/api_hooks/user/uiPreferences'

const STORAGE_KEY = 'careflai.homeTourCompleted.v1'
const PAD = 10
const TOOLTIP_W = 380
const TOOLTIP_GAP = 16

export function hasSeenHomeTour(): boolean {
  try { return localStorage.getItem(STORAGE_KEY) === '1' } catch { return false }
}
function markHomeTourSeen() {
  try { localStorage.setItem(STORAGE_KEY, '1') } catch { /* ignore */ }
}

type CoachStep = {
  id: string
  targetId?: string
  icon: any
  titleKey: string
  bodyKey: string
}

const STEPS: CoachStep[] = [
  { id: 'hero',     targetId: 'home-hero',     icon: LuSparkles,        titleKey: 'home.tour.step.hero.title',     bodyKey: 'home.tour.step.hero.body' },
  { id: 'cases',    targetId: 'home-cases',    icon: LuClipboardList,   titleKey: 'home.tour.step.cases.title',    bodyKey: 'home.tour.step.cases.body' },
  { id: 'activity', targetId: 'home-activity', icon: LuLayoutDashboard, titleKey: 'home.tour.step.activity.title', bodyKey: 'home.tour.step.activity.body' },
  { id: 'sidebar',  targetId: 'sidebar-nav',   icon: LuCompass,         titleKey: 'home.tour.step.sidebar.title',  bodyKey: 'home.tour.step.sidebar.body' },
  { id: 'quick',    targetId: 'sidebar-quick', icon: LuPlus,            titleKey: 'home.tour.step.quick.title',    bodyKey: 'home.tour.step.quick.body' },
  { id: 'aletheia', targetId: 'aletheia-fab',  icon: LuMessageSquare,   titleKey: 'home.tour.step.aletheia.title', bodyKey: 'home.tour.step.aletheia.body' },
  { id: 'user',     targetId: 'sidebar-user',  icon: LuCircleUser,      titleKey: 'home.tour.step.user.title',     bodyKey: 'home.tour.step.user.body' },
]

interface HomeOnboardingTourProps {
  /** -1 closed, 0 welcome, 1..N coachmarks, N+1 final modal. Lifted to the
   *  page-level so the tour state survives any sidebar collapse / re-render. */
  phase: number
  setPhase: (updater: number | ((p: number) => number)) => void
}

export default function HomeOnboardingTour({ phase, setPhase }: HomeOnboardingTourProps) {
  const { t } = useTranslation()
  const [rect, setRect] = useState<DOMRect | null>(null)
  const [tooltipH, setTooltipH] = useState<number>(320)
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const phaseRef = useRef(phase)
  phaseRef.current = phase

  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false
  const { data: prefs, isLoading: prefsLoading } = useUiPreferences()
  const { mutateAsync: updatePrefs } = useUpdateUiPreferences()
  const completedOnServer = !!prefs?.homeTourCompletedAt

  // First-visit auto-open. Server prefs are authoritative — if the server
  // says the tour is NOT completed we always show it, ignoring localStorage
  // (which is per-browser and would otherwise block a fresh user logging
  // into a browser where a *previous* account had already finished the tour).
  // Same reasoning for the AI Act ack: accept either the server value or the
  // local flag, so we don't poll forever when the user acked from another
  // device.
  useEffect(() => {
    if (phaseRef.current !== -1) return
    if (prefsLoading) return
    if (completedOnServer) return

    const aiActAckOnServer = !!prefs?.aiActAcknowledgedAt

    let cancelled = false
    let poll: number | undefined
    const tryOpen = () => {
      if (cancelled) return
      if (!aiActAckOnServer && !hasAcknowledgedAIAct()) {
        poll = window.setTimeout(tryOpen, 800)
        return
      }
      window.setTimeout(() => { if (!cancelled && phaseRef.current === -1) setPhase(0) }, 500)
    }
    tryOpen()
    return () => {
      cancelled = true
      if (poll) window.clearTimeout(poll)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefsLoading, completedOnServer, prefs?.aiActAcknowledgedAt])

  // Track & spotlight the current coachmark target.
  const currentCoach = phase >= 1 && phase <= STEPS.length ? STEPS[phase - 1] : null
  const targetId = currentCoach?.targetId

  useLayoutEffect(() => {
    if (!targetId) { setRect(null); return }
    const el = document.querySelector<HTMLElement>(`[data-tour-id="${targetId}"]`)
    if (!el) { setRect(null); return }
    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
    const measure = () => setRect(el.getBoundingClientRect())
    const t1 = window.setTimeout(measure, 350)
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      window.clearTimeout(t1)
      ro.disconnect()
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [targetId])

  const finish = useCallback((markSeen = true) => {
    if (markSeen) {
      markHomeTourSeen()
      // Best-effort server-side persistence so the dismissal sticks
      // across devices and storage wipes.
      updatePrefs({ homeTourCompletedAt: new Date().toISOString() }).catch(() => {})
    }
    setPhase(-1)
    setRect(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const next = () => setPhase(p => Math.min(p + 1, STEPS.length + 1))
  const prev = () => setPhase(p => Math.max(p - 1, 0))

  if (phase === -1) return null
  if (phase === 0) return <WelcomeModal onStart={() => setPhase(1)} onSkip={() => finish(true)} />
  if (phase === STEPS.length + 1) return <FinishedModal onDone={() => finish(true)} />

  const step = STEPS[phase - 1]
  const total = STEPS.length
  const title = t(step.titleKey)
  const body = t(step.bodyKey)
  const tooltipPos = computeTooltipPos(rect, isMobile, tooltipH)

  return (
    <>
      {/* Backdrop with spotlight cutout (box-shadow trick). */}
      <Box
        position="fixed"
        zIndex={1500}
        pointerEvents="auto"
        {...(rect && {
          top: `${rect.top - PAD}px`,
          left: `${rect.left - PAD}px`,
          width: `${rect.width + PAD * 2}px`,
          height: `${rect.height + PAD * 2}px`,
          borderRadius: '14px',
          boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.62)',
          transition: 'all 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
          outline: '2px solid rgba(168, 85, 247, 0.85)',
          outlineOffset: '2px',
        })}
        {...(!rect && {
          top: 0, left: 0, right: 0, bottom: 0,
          bg: 'blackAlpha.700',
          sx: { backdropFilter: 'blur(4px)' },
        })}
      />

      {/* Tooltip */}
      <Box
        ref={(el: HTMLDivElement | null) => {
          tooltipRef.current = el
          if (el) {
            const h = el.getBoundingClientRect().height
            if (h > 0 && Math.abs(h - tooltipH) > 4) setTooltipH(h)
          }
        }}
        position="fixed"
        zIndex={1501}
        top={`${tooltipPos.top}px`}
        left={`${tooltipPos.left}px`}
        width={`${TOOLTIP_W}px`}
        maxW="calc(100vw - 24px)"
        maxH="calc(100vh - 24px)"
        bg="white"
        borderRadius="2xl"
        boxShadow="0 24px 64px -12px rgba(15, 23, 42, 0.45), 0 0 0 1px rgba(15,23,42,0.08)"
        overflowY="auto"
        overflowX="hidden"
        transition="all 0.3s cubic-bezier(0.22, 1, 0.36, 1)"
      >
        {/* Header — purple/pink gradient matching the HomeHeader brand */}
        <Box
          bgGradient="linear(135deg, #4c1d95 0%, #7c3aed 45%, #db2777 100%)"
          px={4}
          py={3}
          position="relative"
        >
          <HStack justify="space-between" align="center" position="relative" zIndex={1}>
            <HStack spacing={3}>
              <Box
                w="34px" h="34px"
                borderRadius="full"
                bg="whiteAlpha.300"
                border="1px solid"
                borderColor="whiteAlpha.500"
                color="white"
                display="grid"
                sx={{ placeItems: 'center' }}
                fontWeight="800"
                fontSize="sm"
                backdropFilter="blur(8px)"
              >
                {phase}
              </Box>
              <Text fontSize="xs" color="whiteAlpha.900" fontWeight="600" letterSpacing="wider" textTransform="uppercase">
                {t('home.tour.stepCounter', { current: phase, total })}
              </Text>
            </HStack>
            <IconButton
              aria-label={t('home.tour.skip')}
              icon={<LuX />}
              size="xs"
              variant="ghost"
              color="white"
              _hover={{ bg: 'whiteAlpha.300' }}
              onClick={() => finish(true)}
            />
          </HStack>
          <Box
            position="absolute" top={0} left={0} right={0} bottom={0}
            background="radial-gradient(circle at 80% 20%, rgba(255,255,255,0.25) 0%, transparent 55%)"
            pointerEvents="none"
          />
        </Box>

        {/* Body */}
        <VStack align="stretch" spacing={3} px={5} py={4}>
          <HStack spacing={3} align="start">
            <Box
              w="40px" h="40px" minW="40px"
              borderRadius="xl"
              bgGradient="linear(135deg, purple.50, pink.50)"
              border="1px solid"
              borderColor="purple.100"
              display="grid"
              sx={{ placeItems: 'center' }}
              color="purple.600"
            >
              <Icon as={step.icon} boxSize={5} />
            </Box>
            <VStack align="start" spacing={1} flex={1}>
              <Heading size="sm" color="gray.900" lineHeight="1.3">{title}</Heading>
            </VStack>
          </HStack>
          <Text fontSize="sm" color="gray.700" lineHeight="1.65">{body}</Text>

          {/* progress dots */}
          <HStack spacing={1.5} pt={1} justify="center">
            {STEPS.map((_, i) => (
              <Box
                key={i}
                w={i + 1 === phase ? '20px' : '6px'}
                h="6px"
                borderRadius="full"
                bg={i + 1 === phase ? 'purple.500' : i + 1 < phase ? 'purple.200' : 'gray.200'}
                transition="all 0.25s ease"
              />
            ))}
          </HStack>
        </VStack>

        {/* Footer actions */}
        <HStack justify="space-between" px={5} py={3} bg="gray.50" borderTop="1px solid" borderColor="gray.100">
          <Button variant="ghost" size="sm" color="gray.500" onClick={() => finish(true)}>
            {t('home.tour.skip')}
          </Button>
          <HStack spacing={2}>
            {phase > 1 && (
              <Button size="sm" variant="outline" onClick={prev} colorScheme="gray">
                {t('home.tour.prev')}
              </Button>
            )}
            <Button
              size="sm"
              rightIcon={<LuArrowRight />}
              onClick={next}
              bgGradient="linear(135deg, #7c3aed, #db2777)"
              color="white"
              _hover={{ bgGradient: 'linear(135deg, #6d28d9, #c026d3)' }}
            >
              {phase === STEPS.length ? t('home.tour.finish') : t('home.tour.next')}
            </Button>
          </HStack>
        </HStack>
      </Box>
    </>
  )
}

// ── helpers ──────────────────────────────────────────────────────────────

function computeTooltipPos(rect: DOMRect | null, isMobile: boolean, tooltipH = 320) {
  const vw = window.innerWidth
  const vh = window.innerHeight
  const w = Math.min(TOOLTIP_W, vw - 24)

  if (!rect || isMobile) {
    return {
      top: Math.max(24, vh / 2 - tooltipH / 2),
      left: Math.max(12, vw / 2 - w / 2),
    }
  }

  const spaceBelow = vh - rect.bottom
  const spaceAbove = rect.top
  const spaceRight = vw - rect.right
  const spaceLeft = rect.left

  // Sidebar targets sit on the left edge — tooltip preferentially to the
  // right so we don't get clipped off-screen.
  if (rect.left < 280 && spaceRight > w + TOOLTIP_GAP) {
    let top = rect.top + rect.height / 2 - tooltipH / 2
    top = Math.max(12, Math.min(top, vh - tooltipH - 12))
    return { top, left: rect.right + TOOLTIP_GAP }
  }

  // AI chat FAB sits in bottom-right — anchor the tooltip to its left so
  // it stays visible (otherwise it'd clip off the right edge).
  if (rect.right > vw - 120 && spaceLeft > w + TOOLTIP_GAP) {
    let top = rect.top + rect.height / 2 - tooltipH / 2
    top = Math.max(12, Math.min(top, vh - tooltipH - 12))
    return { top, left: rect.left - w - TOOLTIP_GAP }
  }

  const placeBelow = spaceBelow >= tooltipH + TOOLTIP_GAP || spaceBelow >= spaceAbove
  let top = placeBelow ? rect.bottom + TOOLTIP_GAP : rect.top - tooltipH - TOOLTIP_GAP
  top = Math.max(12, Math.min(top, vh - tooltipH - 12))
  let left = rect.left + rect.width / 2 - w / 2
  left = Math.max(12, Math.min(left, vw - w - 12))
  return { top, left }
}

// ── Welcome modal ────────────────────────────────────────────────────────

function WelcomeModal({ onStart, onSkip }: { onStart: () => void; onSkip: () => void }) {
  const { t } = useTranslation()
  const phases = useMemo(() => ([
    { icon: LuLayoutDashboard, color: 'purple', titleKey: 'home.tour.welcome.phase1.title', bodyKey: 'home.tour.welcome.phase1.body' },
    { icon: LuCompass,         color: 'pink',   titleKey: 'home.tour.welcome.phase2.title', bodyKey: 'home.tour.welcome.phase2.body' },
    { icon: LuZap,             color: 'orange', titleKey: 'home.tour.welcome.phase3.title', bodyKey: 'home.tour.welcome.phase3.body' },
    { icon: LuMessageSquare,   color: 'blue',   titleKey: 'home.tour.welcome.phase4.title', bodyKey: 'home.tour.welcome.phase4.body' },
  ]), [])

  return (
    <Modal isOpen onClose={onSkip} size="2xl" isCentered motionPreset="scale">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(6px)" />
      <ModalContent borderRadius="2xl" overflow="hidden">
        <ModalHeader
          bgGradient="linear(135deg, #4c1d95 0%, #7c3aed 45%, #db2777 100%)"
          color="white"
          py={6}
          position="relative"
        >
          <HStack spacing={4} position="relative" zIndex={1}>
            <Box
              w="52px" h="52px"
              borderRadius="xl"
              bg="whiteAlpha.300"
              border="1px solid"
              borderColor="whiteAlpha.500"
              display="grid"
              sx={{ placeItems: 'center' }}
              backdropFilter="blur(10px)"
            >
              <Icon as={LuRocket} boxSize={6} />
            </Box>
            <VStack align="start" spacing={0.5}>
              <Text fontSize="xs" letterSpacing="wider" textTransform="uppercase" color="whiteAlpha.900" fontWeight="700">
                {t('home.tour.welcome.eyebrow')}
              </Text>
              <Heading size="md" letterSpacing="tight">
                {t('home.tour.welcome.title')}
              </Heading>
            </VStack>
          </HStack>
          <Box
            position="absolute" top={0} left={0} right={0} bottom={0}
            background="radial-gradient(circle at 85% 15%, rgba(255,255,255,0.25) 0%, transparent 55%)"
            pointerEvents="none"
          />
        </ModalHeader>

        <ModalBody py={6} px={6}>
          <Text fontSize="sm" color="gray.700" mb={5} lineHeight="1.6">
            {t('home.tour.welcome.subtitle')}
          </Text>

          <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
            {phases.map((p, i) => (
              <Box
                key={i}
                bg="white"
                border="1px solid"
                borderColor="gray.100"
                borderRadius="xl"
                p={4}
                transition="all 0.25s ease"
                _hover={{ transform: 'translateY(-2px)', shadow: 'md', borderColor: `${p.color}.200` }}
              >
                <HStack spacing={3} align="start">
                  <Box
                    w="38px" h="38px"
                    borderRadius="lg"
                    bg={`${p.color}.50`}
                    color={`${p.color}.600`}
                    display="grid"
                    sx={{ placeItems: 'center' }}
                  >
                    <Icon as={p.icon} boxSize={5} />
                  </Box>
                  <VStack align="start" spacing={1} flex={1}>
                    <HStack spacing={2}>
                      <Box
                        w="18px" h="18px"
                        borderRadius="full"
                        bg={`${p.color}.500`}
                        color="white"
                        display="grid"
                        sx={{ placeItems: 'center' }}
                        fontSize="2xs"
                        fontWeight="800"
                      >
                        {i + 1}
                      </Box>
                      <Text fontSize="sm" fontWeight="700" color="gray.900">{t(p.titleKey)}</Text>
                    </HStack>
                    <Text fontSize="xs" color="gray.600" lineHeight="1.5">{t(p.bodyKey)}</Text>
                  </VStack>
                </HStack>
              </Box>
            ))}
          </SimpleGrid>
        </ModalBody>

        <ModalFooter bg="gray.50" borderTop="1px solid" borderColor="gray.100" gap={3}>
          <Button variant="ghost" onClick={onSkip} color="gray.600">
            {t('home.tour.welcome.skip')}
          </Button>
          <Button
            rightIcon={<LuMousePointerClick />}
            onClick={onStart}
            bgGradient="linear(135deg, #7c3aed, #db2777)"
            color="white"
            _hover={{ bgGradient: 'linear(135deg, #6d28d9, #c026d3)' }}
            px={6}
          >
            {t('home.tour.welcome.cta')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

// ── Finish modal ─────────────────────────────────────────────────────────

function FinishedModal({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation()
  return (
    <Modal isOpen onClose={onDone} size="md" isCentered motionPreset="scale">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(6px)" />
      <ModalContent borderRadius="2xl" overflow="hidden">
        <ModalBody pt={8} pb={4} px={6} textAlign="center">
          <Box
            mx="auto" w="64px" h="64px"
            borderRadius="full"
            bgGradient="linear(135deg, #7c3aed, #db2777)"
            display="grid"
            sx={{ placeItems: 'center' }}
            color="white"
            mb={4}
            boxShadow="0 12px 32px -8px rgba(124, 58, 237, 0.55)"
          >
            <Icon as={LuCircleCheck} boxSize={8} />
          </Box>
          <Heading size="md" mb={2} color="gray.900">
            {t('home.tour.finished.title')}
          </Heading>
          <Text fontSize="sm" color="gray.600" lineHeight="1.6">
            {t('home.tour.finished.body')}
          </Text>
        </ModalBody>
        <ModalFooter justifyContent="center" pb={6}>
          <Button
            onClick={onDone}
            bgGradient="linear(135deg, #7c3aed, #db2777)"
            color="white"
            _hover={{ bgGradient: 'linear(135deg, #6d28d9, #c026d3)' }}
            px={8}
          >
            {t('home.tour.finished.cta')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
